import { useEffect, useRef, useState } from "react";
import { api, API, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ShieldCheck, UploadCloud, HardDrive, Wand2, Download, Trash2, Plus, X, Car, User, Scan, RotateCcw } from "lucide-react";
import ZoneEditor from "./ZoneEditor";

const fmtMB = (b) => (b / 1024 ** 2).toFixed(0) + " MB";
const fmtSize = (b) => (b >= 1024 ** 3 ? (b / 1024 ** 3).toFixed(2) + " GB" : fmtMB(b));
const fmtGB = fmtSize;

export default function VideoAnonymizer({ open, onClose }) {
  const [step, setStep] = useState("select"); // select | upload | analyze | faces | process | done
  const [file, setFile] = useState(null);
  const [cap, setCap] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [progress, setProgress] = useState(0);
  const [faces, setFaces] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [chars, setChars] = useState([]);
  const [defaultChar, setDefaultChar] = useState(null);
  const [plateFormat, setPlateFormat] = useState("USA");
  const [plateStart, setPlateStart] = useState("VIVA-1235");
  const [stats, setStats] = useState(null);
  const [jobInfo, setJobInfo] = useState(null);
  const [newCharName, setNewCharName] = useState("");
  const [showCharForm, setShowCharForm] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [resumeUpload, setResumeUpload] = useState(null);
  const [anonPlates, setAnonPlates] = useState(true);
  const [anonFaces, setAnonFaces] = useState(true);
  const [anonZones, setAnonZones] = useState(false);
  const [zones, setZones] = useState([]);
  const [method, setMethod] = useState("blur");
  const pollRef = useRef(null);
  const charFileRef = useRef(null);

  const refreshPending = () =>
    api.get("/anonimizador/trabajos").then((r) => setPendingJobs(r.data)).catch(() => {});

  useEffect(() => {
    if (open) {
      api.get("/anonimizador/personajes").then((r) => {
        setChars(r.data);
        if (r.data.length) setDefaultChar(r.data[0].id);
      }).catch(() => {});
      refreshPending();
    }
    return () => clearInterval(pollRef.current);
  }, [open]);

  const reset = () => {
    clearInterval(pollRef.current);
    setStep("select"); setFile(null); setCap(null); setJobId(null); setMeta(null);
    setProgress(0); setFaces([]); setDecisions({}); setStats(null); setResumeUpload(null);
    setZones([]); setAnonPlates(true); setAnonFaces(true); setAnonZones(false); setMethod("blur");
  };

  const close = (deleteJob) => {
    if (deleteJob && jobId) api.delete(`/anonimizador/trabajos/${jobId}`).catch(() => {});
    reset(); onClose();
  };

  const pickFile = async (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!/\.(mp4|mov|m4v)$/.test(name)) {
      toast.error("Formato no soportado: usa un vídeo MP4 o MOV");
      return;
    }
    if (resumeUpload) {
      if (f.name === resumeUpload.filename && f.size === resumeUpload.size) {
        setFile(f);
        const cs = resumeUpload.chunk_size || 5242880;
        runUpload(resumeUpload.id, cs, Math.floor((resumeUpload.received || 0) / cs), f);
      } else {
        toast.error("El archivo no coincide con la subida pendiente (nombre y tamaño deben ser idénticos)");
      }
      return;
    }
    setFile(f);
    try {
      const r = await api.get(`/anonimizador/capacidad?size=${f.size}`);
      setCap(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const poll = (id, doneStatus, onDone) => {
    clearInterval(pollRef.current);
    let failures = 0;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/anonimizador/trabajos/${id}`, { timeout: 15000 });
        failures = 0;
        setProgress(data.progress);
        setJobInfo(data);
        if (data.status === "ERROR") {
          clearInterval(pollRef.current);
          toast.error(data.error || "Error en el procesamiento");
          setStep(data.faces ? "faces" : "select");
        } else if (data.error && ["SUBIDO", "ANALIZADO"].includes(data.status)) {
          // proceso interrumpido por reinicio del servidor: recuperado, hay que relanzar
          clearInterval(pollRef.current);
          toast.error(data.error);
          setStep(data.status === "ANALIZADO" ? "faces" : "select");
        } else if (data.status === doneStatus) {
          clearInterval(pollRef.current);
          onDone(data);
        }
      } catch {
        failures += 1;
        if (failures >= 10) {
          clearInterval(pollRef.current);
          toast.error("No se puede consultar el estado del trabajo. Comprueba tu conexión y reintenta.");
          setStep("select");
        }
      }
    }, 2000);
  };

  const onAnalyzed = (data) => {
    setFaces(data.faces || []);
    const d = {};
    (data.faces || []).forEach((f) => { d[f.cluster_id] = { anonymize: true, character_id: null }; });
    setDecisions(d);
    setStep("faces");
  };

  const startAnalysis = async (id) => {
    setStep("analyze"); setProgress(0);
    await api.post(`/anonimizador/trabajos/${id}/analizar`);
    poll(id, "ANALIZADO", onAnalyzed);
  };

  const afterUpload = async (id, m = method, facesOn = anonFaces, zonesOn = anonZones) => {
    if (m === "characters" && facesOn) { await startAnalysis(id); return; }
    if (zonesOn) { setStep("faces"); return; }
    await startProcess(id, m);
  };

  const runUpload = async (id, chunkSize, startIndex, f) => {
    try {
      setJobId(id); setStep("upload"); setResumeUpload(null);
      let offset = startIndex * chunkSize;
      let size = chunkSize;
      let retries = 0;
      setProgress(Math.round((offset / f.size) * 100));
      while (offset < f.size) {
        const fd = new FormData();
        fd.append("job_id", id); fd.append("offset", offset);
        fd.append("chunk", f.slice(offset, offset + size));
        try {
          const r = await api.post("/anonimizador/subida/chunk", fd, { timeout: 120000 });
          offset = r.data.received; retries = 0;
          setProgress(Math.round((offset / f.size) * 100));
        } catch (e) {
          const st = e?.response?.status;
          const received = e?.response?.data?.detail?.received;
          if (st === 409 && received != null) { offset = received; continue; }
          if ((st === 413 || st === 502 || !st) && size > 512 * 1024) {
            // el servidor rechaza el trozo: reducir tamaño automáticamente hasta pasar
            size = Math.max(512 * 1024, Math.floor(size / 2));
            toast.message(`Ajustando la subida a trozos de ${(size / 1048576).toFixed(1)} MB por límites del servidor…`);
            continue;
          }
          retries++;
          if (retries > 5) {
            toast.error(`La subida falló (HTTP ${st || "sin respuesta"}). ${errMsg(e)}`);
            throw e;
          }
          await new Promise((r2) => setTimeout(r2, [1000, 3000, 8000][Math.min(retries - 1, 2)]));
          try {
            const stj = await api.get(`/anonimizador/trabajos/${id}`);
            offset = stj.data.received || offset;
          } catch { /* mantiene el offset actual */ }
        }
      }
      const fd = new FormData(); fd.append("job_id", id);
      const done = await api.post("/anonimizador/subida/completar", fd);
      setMeta(done.data.meta);
      await afterUpload(id);
    } catch (e) { toast.error(errMsg(e)); setStep("select"); refreshPending(); }
  };

  const upload = async () => {
    try {
      const init = await api.post("/anonimizador/subida/iniciar", {
        filename: file.name, size: file.size,
        scope: { mode: method, plates: anonPlates, faces: anonFaces, zones: anonZones },
      });
      await runUpload(init.data.job_id, init.data.chunk_size, 0, file);
    } catch (e) { toast.error(errMsg(e)); setStep("select"); }
  };

  const resumeJob = async (job) => {
    try {
      // restaurar el método y alcance con los que se creó el trabajo
      const sc = job.scope || {};
      const m = sc.mode || (job.has_faces ? "characters" : "blur");
      const facesOn = sc.mode ? !!sc.faces : true;
      const zonesOn = sc.mode ? !!sc.zones : false;
      setMethod(m);
      if (sc.mode) { setAnonPlates(!!sc.plates); setAnonFaces(facesOn); setAnonZones(zonesOn); }
      setJobId(job.id); setMeta(job.meta);
      if (job.status === "SUBIENDO") {
        setResumeUpload(job);
        toast.info(`Vuelve a seleccionar "${job.filename}" para continuar la subida donde se quedó (${job.progress}%)`);
        return;
      }
      if (job.status === "SUBIDO") { await afterUpload(job.id, m, facesOn, zonesOn); return; }
      if (job.status === "ANALIZANDO") { setStep("analyze"); setProgress(job.progress); poll(job.id, "ANALIZADO", onAnalyzed); return; }
      if (job.status === "PROCESANDO") { setStep("process"); setProgress(job.progress); poll(job.id, "COMPLETADO", (d) => { setStats(d.stats); setStep("done"); }); return; }
      const { data } = await api.get(`/anonimizador/trabajos/${job.id}`);
      if (data.status === "COMPLETADO") { setStats(data.stats); setStep("done"); }
      else if (data.faces) onAnalyzed(data);
      else { toast.error(data.error || "Este trabajo no se puede continuar"); refreshPending(); }
    } catch (e) { toast.error(errMsg(e)); }
  };

  const discardJob = async (id) => {
    try {
      await api.delete(`/anonimizador/trabajos/${id}`);
      setPendingJobs((p) => p.filter((x) => x.id !== id));
      if (resumeUpload?.id === id) setResumeUpload(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startProcess = async (idOverride, methodOverride) => {
    const id = typeof idOverride === "string" ? idOverride : jobId;
    const m = typeof methodOverride === "string" ? methodOverride : method;
    try {
      await api.post(`/anonimizador/trabajos/${id}/procesar`, {
        faces: m === "characters"
          ? Object.entries(decisions).map(([cluster_id, v]) => ({ cluster_id, ...v }))
          : [],
        mode: m,
        plate_format: plateFormat, plate_start: plateStart, default_character_id: defaultChar,
        anonymize_plates: anonPlates, anonymize_faces: anonFaces,
        zones: anonZones ? zones : [],
      });
      setStep("process"); setProgress(0);
      poll(id, "COMPLETADO", (data) => { setStats(data.stats); setStep("done"); });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addCharacter = async (f) => {
    if (!f || !newCharName.trim()) { toast.error("Pon un nombre al personaje antes de subir su imagen"); return; }
    const fd = new FormData();
    fd.append("name", newCharName.trim()); fd.append("file", f);
    try {
      const r = await api.post("/anonimizador/personajes", fd);
      setChars((c) => [...c, r.data]);
      setNewCharName(""); setShowCharForm(false);
      toast.success("Personaje añadido");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteCharacter = async (id) => {
    try {
      await api.delete(`/anonimizador/personajes/${id}`);
      setChars((c) => c.filter((x) => x.id !== id));
      if (defaultChar === id) setDefaultChar(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const token = localStorage.getItem("natur_token");
  const mediaUrl = jobId ? `${API}/anonimizador/trabajos/${jobId}/media?token=${token}` : "";

  const CharPicker = ({ value, onChange }) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)}
            data-testid="anonymizer-character-select"
            className="h-8 rounded-md border border-neutral-300 px-2 text-xs w-full">
      <option value="">Personaje por defecto</option>
      {chars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="anonymizer-dialog"
                     aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="text-orange-600" size={20} /> Anonimizar Vídeo · Dakota Privacy
          </DialogTitle>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-4">
            {pendingJobs.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="anonymizer-pending-jobs">
                <p className="text-sm font-semibold flex items-center gap-2"><RotateCcw size={14} /> Trabajos en curso</p>
                {pendingJobs.map((pj) => (
                  <div key={pj.id} className="flex items-center gap-2 text-xs" data-testid="anonymizer-pending-job">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{pj.filename}</p>
                      <p className="text-neutral-500">
                        {{ SUBIENDO: "Subida interrumpida", SUBIDO: "Subido · pendiente de análisis",
                           ANALIZANDO: "Analizando", ANALIZADO: "Analizado · pendiente de revisión",
                           PROCESANDO: "Procesando", COMPLETADO: "Completado · listo para descargar",
                           ERROR: "Con error" }[pj.status] || pj.status} · {pj.progress}% · {fmtSize(pj.size)}
                      </p>
                    </div>
                    <Button size="sm" className="h-7 text-xs rounded-full bg-orange-600 hover:bg-orange-700 text-white"
                            data-testid="pending-continue-btn" onClick={() => resumeJob(pj)}>Continuar</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-full"
                            data-testid="pending-discard-btn" onClick={() => discardJob(pj.id)}>Descartar</Button>
                  </div>
                ))}
                <p className="text-[10px] text-neutral-500">Los trabajos pendientes se conservan un máximo de 3 horas y después se eliminan automáticamente.</p>
              </div>
            )}
            <p className="text-sm text-neutral-600">
              Sube un vídeo plano (MP4/MOV, máx 20 GB — recomendado ≤ 5 min).
              Ningún archivo se almacena de forma permanente.
            </p>
            <label
              data-testid="anonymizer-dropzone"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation(); setDragOver(false);
                pickFile(e.dataTransfer.files?.[0]);
              }}
              className={`block border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-orange-500 bg-orange-50" : "border-neutral-300 hover:border-orange-400"}`}>
              <UploadCloud className={`mx-auto mb-2 ${dragOver ? "text-orange-500" : "text-neutral-400"}`} size={32} />
              <span className="text-sm font-medium">
                {dragOver ? "Suelta el vídeo aquí" : file ? file.name : "Arrastra tu vídeo aquí o haz clic para seleccionarlo (MP4 / MOV)"}
              </span>
              {file && <span className="block text-xs text-neutral-500 mt-1">{fmtGB(file.size)}</span>}
              <input type="file" accept=".mp4,.mov,.m4v,video/mp4,video/quicktime" className="hidden"
                     data-testid="anonymizer-file-input" onChange={(e) => pickFile(e.target.files[0])} />
            </label>
            {cap && (
              <div className={`rounded-xl border p-4 text-sm space-y-1 ${cap.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
                   data-testid="anonymizer-capacity-panel">
                <p className="flex items-center gap-2 font-medium"><HardDrive size={15} />
                  Estado de capacidad: {cap.ok ? "✅ Suficiente" : "⛔ Bloqueado"}</p>
                <p>Espacio disponible: <b>{fmtGB(cap.available_bytes)}</b> · Espacio requerido: <b>{fmtGB(cap.required_bytes)}</b></p>
                {!cap.ok && <p className="text-red-700 font-medium">Motivo: {cap.reason}</p>}
              </div>
            )}
            <div className="rounded-xl border border-neutral-200 p-4 space-y-2" data-testid="anonymizer-scope-selector">
              <p className="text-sm font-semibold">¿Qué quieres anonimizar?</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={anonPlates} data-testid="scope-plates-toggle"
                       onChange={(e) => setAnonPlates(e.target.checked)} />
                <Car size={15} className="text-neutral-500" /> Matrículas <span className="text-xs text-neutral-400">(recomendado — cobertura opaca irreversible con matrícula ficticia)</span>
              </label>
              {!anonPlates && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="scope-plates-warning">
                  ⚠️ Confirmas que este vídeo NO contiene matrículas visibles. La responsabilidad de su publicación es tuya. Esta decisión queda registrada en la auditoría.
                </p>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={anonFaces} data-testid="scope-faces-toggle"
                       onChange={(e) => setAnonFaces(e.target.checked)} />
                <User size={15} className="text-neutral-500" /> Rostros <span className="text-xs text-neutral-400">(sustitución por personajes; desmárcalo si no hay personas y el proceso será mucho más rápido)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={anonZones} data-testid="scope-zones-toggle"
                       onChange={(e) => setAnonZones(e.target.checked)} />
                <Scan size={15} className="text-neutral-500" /> Otros: zonas manuales <span className="text-xs text-neutral-400">(dibujarás rectángulos sobre un fotograma — GPS, rótulos, pantallas…)</span>
              </label>
            </div>
            <div className="rounded-xl border border-neutral-200 p-4 space-y-2" data-testid="anonymizer-method-selector">
              <p className="text-sm font-semibold">Método</p>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="anon-method" className="mt-0.5" checked={method === "blur"}
                       data-testid="method-blur-radio" onChange={() => setMethod("blur")} />
                <span><b>Difuminado rápido</b> <span className="text-xs text-neutral-400">(recomendado) — pixelado irreversible de todos los rostros y matrículas detectados. Sin pasos extra, mínimo consumo: subir → procesar → descargar.</span></span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="radio" name="anon-method" className="mt-0.5" checked={method === "characters"}
                       data-testid="method-characters-radio" onChange={() => setMethod("characters")} />
                <span><b>Personajes</b> <span className="text-xs text-neutral-400">(extra) — analiza el vídeo, eliges qué rostros se ven y cuáles se cubren con elfos/hadas, y las matrículas se sustituyen por ficticias.</span></span>
              </label>
            </div>
            <Button disabled={!file || !cap?.ok || (!anonPlates && !anonFaces && !anonZones)} onClick={upload} data-testid="anonymizer-upload-btn"
                    className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white">
              Subir vídeo
            </Button>
          </div>
        )}

        {(step === "upload" || step === "analyze" || step === "process") && (
          <div className="py-8 text-center space-y-4" data-testid="anonymizer-progress-panel">
            <p className="text-sm font-medium">
              {step === "upload" && `Subiendo vídeo (${fmtMB(file?.size * progress / 100 || 0)} de ${fmtMB(file?.size || 0)})…`}
              {step === "analyze" && "Analizando vídeo… Dakota está detectando rostros y matrículas"}
              {step === "process" && "Anonimizando vídeo… cubriendo rostros y matrículas fotograma a fotograma"}
            </p>
            <Progress value={progress} className="h-3" />
            <p className="text-2xl font-semibold text-orange-600">[ {progress}% ]</p>
            {step === "process" && jobInfo?.phase === "VERIFICANDO" && (
              <p className="text-sm text-emerald-700" data-testid="anonymizer-verifying-info">
                Doble verificación en curso: re-analizando el resultado en busca de matrículas legibles…
              </p>
            )}
            {step === "process" && jobInfo?.frames_done > 0 && (
              <p className="text-sm text-neutral-600" data-testid="anonymizer-frames-info">
                {jobInfo.frames_done.toLocaleString()} de {(jobInfo.frames_total || 0).toLocaleString()} fotogramas
                {jobInfo.elapsed_sec > 5 && jobInfo.frames_total > jobInfo.frames_done && (
                  <> · quedan ~{Math.max(1, Math.round((jobInfo.elapsed_sec / jobInfo.frames_done) * (jobInfo.frames_total - jobInfo.frames_done) / 60))} min</>
                )}
              </p>
            )}
            {meta && step !== "upload" && (
              <p className="text-xs text-neutral-500">
                {meta.width}×{meta.height} · {Math.round(meta.duration)}s · {meta.fps} fps
                {step === "process" && " · Este proceso puede tardar varios minutos, no cierres esta ventana"}
              </p>
            )}
          </div>
        )}

        {step === "faces" && (
          <div className="space-y-5">
            {!(method === "characters" && anonFaces) && !(anonPlates && method === "characters") && !anonZones && (
              <p className="text-sm text-neutral-500" data-testid="anonymizer-nothing-to-review">
                Nada que revisar con la configuración actual. Pulsa "Anonimizar vídeo" para continuar.
              </p>
            )}
            {method === "characters" && anonFaces && (<>
            <div>
              <h3 className="font-semibold text-sm mb-2">Rostros detectados ({faces.length})</h3>
              {faces.length === 0 && <p className="text-sm text-neutral-500">No se detectaron rostros en el análisis.</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {faces.map((f) => {
                  const d = decisions[f.cluster_id] || {};
                  return (
                    <div key={f.cluster_id} data-testid="anonymizer-face-card"
                         className={`rounded-xl border p-3 space-y-2 ${d.anonymize ? "border-orange-300 bg-orange-50/50" : "border-neutral-200"}`}>
                      <img src={f.thumb} alt="rostro" className="w-full h-24 object-cover rounded-lg" />
                      <p className="text-[11px] text-neutral-500">{f.appearances} aparición(es)</p>
                      <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                        <input type="checkbox" checked={!!d.anonymize} data-testid="anonymizer-face-toggle"
                               onChange={(e) => setDecisions({ ...decisions, [f.cluster_id]: { ...d, anonymize: e.target.checked } })} />
                        Anonimizar
                      </label>
                      {d.anonymize && <CharPicker value={d.character_id}
                        onChange={(v) => setDecisions({ ...decisions, [f.cluster_id]: { ...d, character_id: v } })} />}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Personajes</h3>
                <Button variant="outline" size="sm" className="rounded-full text-xs h-7"
                        data-testid="anonymizer-add-character-btn" onClick={() => setShowCharForm(!showCharForm)}>
                  <Plus size={13} className="mr-1" /> Añadir personaje
                </Button>
              </div>
              {showCharForm && (
                <div className="flex gap-2 mb-3">
                  <Input placeholder="Nombre del personaje" value={newCharName} className="h-8 text-xs"
                         data-testid="anonymizer-character-name-input" onChange={(e) => setNewCharName(e.target.value)} />
                  <Button size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={() => charFileRef.current.click()}>
                    Subir PNG
                  </Button>
                  <input ref={charFileRef} type="file" accept="image/png,image/webp" className="hidden"
                         onChange={(e) => addCharacter(e.target.files[0])} />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {chars.map((c) => (
                  <button key={c.id} onClick={() => setDefaultChar(c.id)} data-testid="anonymizer-character-chip"
                          className={`relative group rounded-xl border p-2 w-16 ${defaultChar === c.id ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${c.url}`} alt={c.name} className="w-10 h-10 object-contain mx-auto" />
                    <span className="block text-[10px] mt-1 truncate">{c.name}</span>
                    {!c.system && (
                      <span onClick={(e) => { e.stopPropagation(); deleteCharacter(c.id); }}
                            className="absolute -top-1.5 -right-1.5 hidden group-hover:flex bg-red-500 text-white rounded-full w-4 h-4 items-center justify-center">
                        <X size={10} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">El personaje seleccionado se usa por defecto para rostros sin personaje asignado.</p>
            </div>
            </>)}

            {anonPlates && method === "characters" && (
            <div>
              <h3 className="font-semibold text-sm mb-2">Matrículas ficticias</h3>
              <div className="flex gap-2">
                <select value={plateFormat} onChange={(e) => setPlateFormat(e.target.value)}
                        data-testid="anonymizer-plate-format-select"
                        className="h-9 rounded-md border border-neutral-300 px-2 text-sm">
                  <option value="USA">🇺🇸 Estilo USA</option>
                  <option value="ES">🇪🇸 Estilo España / UE</option>
                </select>
                <Input value={plateStart} onChange={(e) => setPlateStart(e.target.value)} className="h-9 max-w-[180px]"
                       data-testid="anonymizer-plate-start-input" placeholder="Matrícula inicial" />
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">
                Cada matrícula real distinta recibirá la siguiente de la serie: {plateStart} → … Los píxeles originales se destruyen (irreversible).
              </p>
            </div>
            )}

            {anonZones && (
            <div>
              <h3 className="font-semibold text-sm mb-2">Zonas manuales ({zones.length})</h3>
              <p className="text-xs text-neutral-500 mb-2">
                Dibuja rectángulos sobre el fotograma (clic y arrastra). Esas zonas quedarán cubiertas de forma opaca durante TODO el vídeo.
              </p>
              <ZoneEditor imageUrl={`${API}/anonimizador/trabajos/${jobId}/fotograma?token=${token}`}
                          zones={zones} onChange={setZones} />
            </div>
            )}

            <Button onClick={startProcess} data-testid="anonymizer-process-btn"
                    className="w-full rounded-full bg-neutral-900 text-white">
              <Wand2 size={15} className="mr-2" /> Anonimizar vídeo
            </Button>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-4" data-testid="anonymizer-done-panel">
            <video src={mediaUrl} controls className="w-full rounded-xl bg-black max-h-[45vh]" data-testid="anonymizer-preview-video" />
            {stats && (
              <p className="text-xs text-neutral-500">
                {stats.plates_replaced} matrícula(s) cubierta(s){stats.zones > 0 ? ` · ${stats.zones} zona(s) manual(es)` : ""} · {stats.frames} fotogramas ·
                {" "}{stats.output_mb} MB · procesado en {stats.processing_sec}s
              </p>
            )}
            {stats?.verification && (stats.verification.suspects === 0 ? (
              <p className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="anonymizer-verification-ok">
                ✅ Doble verificación superada: 0 matrículas legibles detectadas ({stats.verification.frames_checked} fotogramas revisados)
              </p>
            ) : (
              <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="anonymizer-verification-warn">
                ⚠️ La doble verificación encontró {stats.verification.suspects} posible(s) matrícula(s) aún visible(s). Revisa el vídeo antes de publicarlo.
              </p>
            ))}
            <a href={`${mediaUrl}&dl=1`} data-testid="anonymizer-download-btn"
               className="flex items-center justify-center gap-2 w-full h-10 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium">
              <Download size={15} /> Descargar vídeo anonimizado
            </a>
            <p className="text-[11px] text-neutral-500 text-center">
              Al completarse la descarga, todos los archivos se eliminan automáticamente del servidor.
            </p>
            <Button variant="outline" onClick={() => close(true)} data-testid="anonymizer-finish-btn"
                    className="w-full rounded-full">
              <Trash2 size={14} className="mr-2" /> Finalizar y borrar archivos
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
